import React, { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';
import posed, { PoseGroup } from 'react-pose';
import { css } from '@emotion/core';
import config from 'config';

import { formatCurrency, casualName } from 'utils';
import Avatar from 'common/avatar';
import Button from 'common/button';
import Icon from 'common/icon';
import Markdown from 'common/markdown';
import Modal from 'common/modal';

const CheckedIconPose = posed.div({
    enter: { scale: 1, opacity: 1, duration: 100 },
    exit: { scale: 0.9, opacity: 0, duration: 100 },
});

export default class ContributeConfirm extends Component {
    static propTypes = {
        onConfirm: PropTypes.func.isRequired,
        campaign: PropTypes.shape({
            name: PropTypes.string.isRequired,
            thirdPartyTerms: PropTypes.shape({
                statement: PropTypes.string,
                extended: PropTypes.string,
            }),
            stats: PropTypes.shape({
                performanceRemaining: PropTypes.number.isRequired,
                performanceEstimatedTotal: PropTypes.number.isRequired,
            }).isRequired,
        }).isRequired,

        contribution: PropTypes.shape({
            amount: PropTypes.number.isRequired,
            type: PropTypes.oneOf(['pledge', 'donate']).isRequired,
            backpledge: PropTypes.bool.isRequired,
            tip: PropTypes.number,
            card: PropTypes.shape({
                brand: PropTypes.string.isRequired,
                last4: PropTypes.string.isRequired,
            }).isRequired,
            thirdPartyTermsAccepted: PropTypes.bool,
        }).isRequired,

        fundraiser: PropTypes.shape({
            user: PropTypes.shape({
                firstName: PropTypes.string,
                lastName: PropTypes.string,
                avatar: PropTypes.shape({
                    id: PropTypes.string,
                }),
            }),
        }),

        onThirdPartyAccepted: PropTypes.func.isRequired,
    };

    static defaultProps = {
        fundraiser: null,
    };

    state = {
        isModalOpen: false,
        isSubmitting: false,
    };

    get fundraiserAvatar() {
        const { fundraiser } = this.props;
        return fundraiser ? get(fundraiser, 'user.avatar.id') : null;
    }

    get totalLabel() {
        const { contribution } = this.props;
        return contribution.type === 'pledge' ? 'Your estimated total' : 'Your total';
    }

    get total() {
        const { contribution, campaign } = this.props;
        const tipAmount = contribution.tip || 0;

        if (contribution.type === 'donate') {
            return tipAmount + contribution.amount;
        }

        const metricValue = contribution.backpledge
            ? get(campaign, 'stats.performanceEstimatedTotal', 0)
            : get(campaign, 'stats.performanceRemaining', 0);

        return tipAmount + contribution.amount * metricValue;
    }

    get cardIcon() {
        const { contribution } = this.props;

        switch (contribution.card.brand) {
            case 'Visa':
                return 'cc-visa';
            case 'Mastercard':
                return 'cc-mastercard';
            case 'Discover':
                return 'cc-discover';
            case 'American Express':
                return 'cc-amex';
            default:
                return 'cc-stripe';
        }
    }

    get buttonLabel() {
        const { contribution } = this.props;
        return contribution.type === 'pledge' ? 'Confirm Pledge' : 'Confirm Donation';
    }

    /**
     * Toggle the modal open and close.
     *
     * @fires
     *  this.setState on isModalOpen
     */
    toggleModalOpen = () =>
        this.setState(prev => ({
            isModalOpen: !prev.isModalOpen,
        }));

    render() {
        const { campaign, fundraiser, contribution, onConfirm, onThirdPartyAccepted } = this.props;
        const { isModalOpen, isSubmitting } = this.state;
        const { card, thirdPartyTermsAccepted } = contribution;
        const { thirdPartyTerms } = campaign;

        const hasThirdPartyTerms = !isEmpty(campaign.thirdPartyTerms);

        return (
            <div className="mx-auto" css={{ maxWidth: 480 }}>
                <div className="bg-white rounded-lg shadow-md py-12 px-8 xxxl:py-16 md:px-24">
                    <div className="text-center border-b-3 border-gray-20 pb-8 mb-8">
                        <p className="text-12 uppercase text-gray-30 tracking-wide block font-bold">
                            Contributing to
                        </p>
                        <h1 className="text-18 font-bold truncate text-gray-70 mt-2">
                            {campaign.name}
                        </h1>

                        {fundraiser && (
                            <div
                                className="font-medium text-14 text-gray-70 mt-4 md:flex justify-center items-center sm:block"
                                data-testid="fundraiser"
                            >
                                <p className="sm:mb-2 md:inline-block">Give credit to:</p>
                                <div className="inline-flex items-center md:ml-2">
                                    {this.fundraiserAvatar && (
                                        <Avatar
                                            imageId={this.fundraiserAvatar}
                                            firstName={fundraiser.user.firstName}
                                            lastName={fundraiser.user.lastName}
                                            className="mr-2 w-12 h-12 rounded-full"
                                        />
                                    )}
                                    <span>
                                        {casualName(
                                            fundraiser.user.firstName,
                                            fundraiser.user.lastName
                                        )}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="font-medium text-16 text-gray-70">
                        <div className="flex justify-between items-center mb-8">
                            <p>{this.totalLabel}</p>
                            <p className="text-right">{formatCurrency(this.total)}</p>
                        </div>
                        <div className="flex justify-between items-center">
                            <p>Credit/debit card</p>
                            <p className="text-right inline-flex items-center">
                                <Icon
                                    icon={this.cardIcon}
                                    type="brand"
                                    className="text-gray-40 text-28 mr-4"
                                />
                                <span className="inline-block">{card.last4}</span>
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 lg:mx-16">
                    <Button
                        type="button"
                        size="md"
                        color="green"
                        className={cx('text-16 font-medium w-full', {
                            'opacity-40 pointer-events-none': isSubmitting,
                        })}
                        onClick={() =>
                            onConfirm({
                                setSubmitting: x => this.setState({ isSubmitting: x }),
                            })
                        }
                    >
                        {isSubmitting ? (
                            <Icon icon="spinner" className="text-18" spin />
                        ) : (
                            this.buttonLabel
                        )}
                    </Button>
                </div>
                {hasThirdPartyTerms && (
                    <div
                        className="text-center text-12 font-medium mt-8 pb-8 border-b border-gray-20"
                        data-testid="thirdPartyConsent"
                    >
                        <button
                            type="button"
                            onClick={onThirdPartyAccepted}
                            className="w-full flex text-gray-40 leading-normal"
                            data-testid="thirdPartyConsentButton"
                        >
                            <div className="text-xl">
                                <PoseGroup withParent={false} animateOnMount>
                                    <CheckedIconPose
                                        key={thirdPartyTermsAccepted ? 'icon1' : 'icon2'}
                                    >
                                        <Icon
                                            icon={
                                                thirdPartyTermsAccepted ? 'check-square' : 'square'
                                            }
                                            type="regular"
                                            fixedWidth
                                            className="text-green align-top"
                                            data-testid="thirdPartyIcon"
                                        />
                                    </CheckedIconPose>
                                </PoseGroup>
                            </div>
                            <div className="pl-4">{thirdPartyTerms.statement}</div>
                        </button>

                        {thirdPartyTerms.extended && (
                            <p className="mt-4">
                                <button
                                    type="button"
                                    className="link-green cursor-pointer"
                                    onClick={this.toggleModalOpen}
                                >
                                    Click for full details
                                </button>
                            </p>
                        )}
                    </div>
                )}

                <p className="font-medium text-center text-12 text-gray-40 mt-8">
                    By confirming you agree to Pledge It’s{' '}
                    <a href="/tos" target="_blank" className="link-green">
                        Terms of Service
                    </a>
                </p>

                <Modal close={this.toggleModalOpen} isOpen={isModalOpen}>
                    <div className="p-12">
                        <Markdown
                            css={css`
                                a {
                                    color: ${config('/tailwind/colors/green50')};

                                    &:hover {
                                        color: ${config('/tailwind/colors/green70')};
                                    }
                                }
                                p,
                                ul,
                                ol {
                                    + p,
                                    + ul,
                                    + ol {
                                        margin-top: ${config('/tailwind/margin/8')};
                                    }
                                }
                            `}
                        >
                            {thirdPartyTerms && thirdPartyTerms.extended}
                        </Markdown>
                        <Button
                            type="button"
                            size="md"
                            color="gray"
                            className="mt-8 text-16 font-medium w-full text-white"
                            onClick={this.toggleModalOpen}
                        >
                            Close
                        </Button>
                    </div>
                </Modal>
            </div>
        );
    }
}
